import type { TeamRoleOption } from "@domain/CRM/teamMember";
import { UserSettings } from "@domain/Settings";
/**
 * Proagrica OAuth token shape stored on the user. Owned by the user domain
 * because it lives on the User entity; the infrastructure layer re-exports it
 * for consumers that interact with the Proagrica API directly.
 */
export interface ProagricaAuth {
	access_token: string;
	expires_in: number;
	id_token: string;
	refresh_token: string;
	scope: string;
	token_type: string;
}

export type Role = TeamRoleOption | "Customer";

export interface IUser {
	id: string;
	name: string;
	token: string;
	username: string;
	email: string;
	fullName: string;
	disabled: boolean;
	companyIds: string[];
	companyIdsAll: string[];
	portalSettings: UserSettings;
	portalImportOptions: string[];
	roles: Role[];
	proagricaAuth?: ProagricaAuth;
}

export class User implements IUser {
	id: string;
	name: string;
	token: string;
	username: string;
	email: string;
	fullName: string;
	disabled: boolean;
	companyIds: string[];
	companyIdsAll: string[];
	portalSettings: UserSettings;
	portalImportOptions: string[];
	roles: Role[];
	proagricaAuth?: ProagricaAuth;

	/**
	 * Fields are copied individually rather than `Object.assign`-ed so that a `data` carrying extra
	 * properties (an API payload, say) can't smuggle them onto the entity.
	 */
	constructor(data: IUser) {
		this.id = data.id;
		this.name = data.name;
		this.token = data.token;
		this.username = data.username;
		this.email = data.email;
		this.fullName = data.fullName;
		this.disabled = data.disabled;
		this.companyIds = data.companyIds;
		this.companyIdsAll = data.companyIdsAll;
		this.portalSettings = data.portalSettings;
		this.portalImportOptions = data.portalImportOptions;
		this.roles = data.roles;
		this.proagricaAuth = data.proagricaAuth;
	}

	static fromIUser(data: IUser, token?: string): User {
		return new User({ ...data, token: token ?? data.token });
	}

	get isDeveloper() {
		return this.roles.includes("Developer");
	}

	get isSoftwareTester() {
		return this.roles.includes("Software Tester") || this.isDeveloper;
	}

	get isAdmin() {
		return this.roles.includes("App admin") || this.isDeveloper;
	}

	/** Rogo staff, by email domain — mirrors the backend `is_rogo_user` (excludes `+` test accounts).
	 * Ops actions (portal cache clear) are gated to this population server-side. */
	get isRogo() {
		return this.email.endsWith("@rogoag.com") && !this.email.includes("+");
	}

	get isOfficePersonnel() {
		return this.roles.includes("Office") || this.isAdmin;
	}

	get isMapmaker() {
		return this.roles.includes("Mapmaker") || this.isAdmin;
	}

	get isCustomer() {
		return this.roles.includes("Customer") || this.isAdmin;
	}

	get isSalesperson() {
		return this.roles.includes("Sales") || this.isAdmin;
	}
}
